import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import { MutableRefObject, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useChatSessionsContext } from "../lib/contexts/ChatSessionsContext";
import { useCustomThemeContext } from "../lib/contexts/ThemeContext";
import { ChatSession } from "../lib/interfaces/Chat";

interface ChatSessionHistoryProps {
  handleDelete: (chatId: string) => void;
  handleRename: (chatId: string, newTitle: string) => void;
  deleteChatId: string;
  renameChatId: string;
  isLoading: MutableRefObject<boolean>;
  sessionsGroupedByMonths: Record<string, ChatSession[]>;
  windowSize: { width: number };
}

export default function ChatSessionHistory({
  handleDelete,
  handleRename,
  deleteChatId,
  renameChatId,
  isLoading,
  sessionsGroupedByMonths,
  windowSize,
}: ChatSessionHistoryProps) {
  const { custom } = useCustomThemeContext();
  const [editingChatId, setEditingChatId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState<string>("");
  const { updateSelectedChatSession, selectedChatSession, handleDrawerClose } =
    useChatSessionsContext();

  let navigate = useNavigate();

  const handleEditClick = (chatId: string, currentTitle: string) => {
    setEditingChatId(chatId);
    setEditTitle(currentTitle);
  };

  const handleEditConfirm = (chatId: string) => {
    if (editTitle.trim()) {
      handleRename(chatId, editTitle.trim());
      setEditingChatId(null);
      setEditTitle("");
    }
  };

  const handleEditCancel = () => {
    setEditingChatId(null);
    setEditTitle("");
  };

  const handleKeyPress = (e: React.KeyboardEvent, chatId: string) => {
    if (e.key === "Enter") {
      handleEditConfirm(chatId);
    } else if (e.key === "Escape") {
      handleEditCancel();
    }
  };

  const handleChatSessionClick = (chatId: string) => {
    isLoading.current = false;
    updateSelectedChatSession(chatId);
    handleDrawerClose();
    navigate(`/chat/${chatId}`);
  };

  if (windowSize.width <= 10) {
    // Mobile view - flatten sessions
    const flattenedSessions = Object.values(
      sessionsGroupedByMonths
    ).flat() as ChatSession[];

    return (
      <List>
        {flattenedSessions.map((chatSession: ChatSession) => (
          <ListItem key={chatSession.chatInfo.chatId} disablePadding>
            <ListItemButton
              onClick={() =>
                handleChatSessionClick(chatSession.chatInfo.chatId)
              }
            >
              <ListItemText
                primary={chatSession.chatInfo.chatTitle}
                primaryTypographyProps={{
                  style: {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    );
  }

  // Desktop view - grouped by months
  return (
    <List>
      {Object.keys(sessionsGroupedByMonths).map((month) => (
        <Box key={month}>
          <Typography
            variant="button"
            sx={{
              color: custom.palette.primary.light,
              fontWeight: "bold",
              letterSpacing: "0.08em",
              fontSize: "0.9rem",
            }}
          >
            {month}
          </Typography>
          {sessionsGroupedByMonths[month].map((chatSession: ChatSession) => (
            <ListItem key={chatSession.chatInfo.chatId} disablePadding>
              <ListItemButton
                sx={{
                  "& .MuiSvgIcon-fontSizeMedium": {
                    display: "none",
                  },
                  "&:hover": {
                    "& .MuiSvgIcon-fontSizeMedium": {
                      display: "inline-block",
                    },
                  },
                }}
                selected={chatSession.chatInfo.chatId === selectedChatSession}
                onClick={() =>
                  handleChatSessionClick(chatSession.chatInfo.chatId)
                }
              >
                <ListItemText
                  primary={
                    editingChatId === chatSession.chatInfo.chatId ? (
                      <TextField
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        onKeyDown={(e) =>
                          handleKeyPress(e, chatSession.chatInfo.chatId)
                        }
                        onBlur={() => handleEditCancel()}
                        autoFocus
                        variant="standard"
                        size="small"
                        sx={{
                          "& .MuiInput-input": {
                            fontSize: "inherit",
                            padding: 0,
                          },
                        }}
                      />
                    ) : (
                      chatSession.chatInfo.chatTitle
                    )
                  }
                  primaryTypographyProps={{
                    style: {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                  }}
                />
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <ListItemIcon
                    sx={{ minWidth: "auto", mr: 0 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditClick(
                        chatSession.chatInfo.chatId,
                        chatSession.chatInfo.chatTitle
                      );
                    }}
                  >
                    {renameChatId !== chatSession.chatInfo.chatId ? (
                      <EditOutlinedIcon />
                    ) : (
                      <CircularProgress
                        size={16}
                        sx={{
                          color: custom.palette.primary.light,
                        }}
                      />
                    )}
                  </ListItemIcon>
                  <ListItemIcon
                    sx={{ minWidth: "auto", mr: 0 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      const confirmed = window.confirm(
                        `Are you sure you want to delete "${chatSession.chatInfo.chatTitle}"?`
                      );
                      if (confirmed) {
                        handleDelete(chatSession.chatInfo.chatId);
                      }
                    }}
                  >
                    {deleteChatId !== chatSession.chatInfo.chatId ? (
                      <DeleteOutlineIcon />
                    ) : (
                      <CircularProgress
                        size={16}
                        sx={{
                          color: custom.palette.primary.light,
                        }}
                      />
                    )}
                  </ListItemIcon>
                </Box>
              </ListItemButton>
            </ListItem>
          ))}
        </Box>
      ))}
    </List>
  );
}
